/**
 * CollisionWorkerService
 * Main thread manager for the collision detection web worker
 *
 * Owns the Worker instance, handles the message protocol with monotonic checkId
 * sequencing, provides async collision check API, and auto-falls back to
 * synchronous CollisionService on any worker error.
 */

import * as THREE from 'three'
import type {
  WorkerOutMessage,
  WorkerInMessage,
  SerializedAircraftGeometry,
  SerializedWallGeometry,
  SerializedObstacleGeometry,
  CollisionResultMessage,
} from './collision.worker.types'
import {
  serializeAircraftForWorker,
  serializeWallMeshes,
  collectTransferables,
  collectWallTransferables,
  readAircraftTransforms,
} from './GeometrySerializer'
import {
  createHangarWallMeshes,
  getAggregatedWallCollision,
} from '../HangarWallCollisionBuilder'
import { createAllUnitWallMeshes, getAggregatedUnitWallCollision } from '../UnitWallCollisionBuilder'
import { checkCollisions } from '../CollisionService'
import { useCollisionStore } from '@/stores/collisionStore'
import { COLLISION_CHECK_RADIUS_METERS } from '@/constants/collision'
import type { CollisionResult, HangarCollisionConfig, UnitCollisionConfig, WallCollisionInfo, ObstacleCollisionInfo } from '@/types/collision'
import type { PlacedAircraft3D } from '@/services/3d'

type PendingCheck = {
  resolve: (result: CollisionResult) => void
  reject: (error: Error) => void
  checkId: number
  aircraftIds: string[]
  movingAircraftId?: string
}

export class CollisionWorkerService {
  private worker: Worker | null = null
  private isReady = false
  private geometryReady = false
  private nextCheckId = 1
  private lastResolvedCheckId = 0
  private pendingChecks = new Map<number, PendingCheck>()
  private fallbackMode = false
  private initPromise: Promise<void> | null = null
  private initResolve: (() => void) | null = null

  /** Whether worker mode is enabled */
  get isWorkerMode(): boolean {
    return this.worker !== null && !this.fallbackMode
  }

  /** Whether worker is ready to accept collision checks */
  get ready(): boolean {
    return this.isReady && this.geometryReady
  }

  /**
   * Initialize the worker
   * Returns a promise that resolves when the worker signals WORKER_READY
   */
  async init(): Promise<void> {
    if (this.worker) return

    try {
      // Vite handles ?worker import — creates a module worker
      this.worker = new Worker(
        new URL('./collision.worker.ts', import.meta.url),
        { type: 'module' }
      )

      this.initPromise = new Promise<void>((resolve) => {
        this.initResolve = resolve
      })

      this.worker.onmessage = (event: MessageEvent<WorkerOutMessage>) => {
        this.handleMessage(event.data)
      }

      this.worker.onerror = (event) => {
        console.error('[CollisionWorker] Worker error:', event.message)
        this.enableFallback()
      }

      await this.initPromise
    } catch (error) {
      console.warn('[CollisionWorker] Failed to create worker, using fallback:', error)
      this.enableFallback()
    }
  }

  /**
   * Send aircraft geometry to the worker for BVH construction
   */
  async initGeometry(
    aircraft: PlacedAircraft3D[],
    sceneObjects: Map<string, THREE.Object3D>
  ): Promise<void> {
    if (this.fallbackMode || !this.worker) return

    const serialized: SerializedAircraftGeometry[] = []

    for (const plane of aircraft) {
      const obj = sceneObjects.get(plane.id)
      if (!obj) continue
      serialized.push(serializeAircraftForWorker(plane.id, plane.typeId, obj))
    }

    const transferables = collectTransferables(serialized)

    const msg: WorkerInMessage = {
      type: 'INIT_GEOMETRY',
      aircraft: serialized,
    }

    return new Promise<void>((resolve) => {
      // Wait for GEOMETRY_READY from INIT_GEOMETRY specifically (not wall/obstacle updates)
      const onReady = (event: MessageEvent<WorkerOutMessage>) => {
        if (event.data.type === 'GEOMETRY_READY' && event.data.source === 'init') {
          this.geometryReady = true
          this.worker?.removeEventListener('message', onReady)
          if (import.meta.env.DEV) {
            console.log(`[CollisionWorker] Init geometry ready: ${event.data.aircraftCount} aircraft, ${event.data.wallCount} walls`)
          }
          resolve()
        }
      }
      this.worker!.addEventListener('message', onReady)
      this.worker!.postMessage(msg, transferables)
    })
  }

  /**
   * Update a single aircraft's geometry (e.g., after model load)
   */
  updateAircraftGeometry(
    id: string,
    typeId: string,
    object: THREE.Object3D
  ): void {
    if (this.fallbackMode || !this.worker) return

    const serialized = serializeAircraftForWorker(id, typeId, object)
    const transferables = collectTransferables([serialized])

    const msg: WorkerInMessage = {
      type: 'UPDATE_AIRCRAFT_GEOMETRY',
      aircraft: serialized,
    }

    this.worker.postMessage(msg, transferables)
  }

  /**
   * Remove an aircraft from the worker
   */
  removeAircraft(id: string): void {
    if (this.fallbackMode || !this.worker) return

    const msg: WorkerInMessage = { type: 'REMOVE_AIRCRAFT', id }
    this.worker.postMessage(msg)
  }

  /**
   * Send wall geometry to the worker
   */
  updateWallGeometry(
    hangarConfigs: HangarCollisionConfig[],
    unitConfigs: UnitCollisionConfig[]
  ): void {
    if (this.fallbackMode || !this.worker) return

    // Build hangar wall meshes on main thread, serialize for worker
    const hangarWalls = new Map<string, ReturnType<typeof createHangarWallMeshes>>()
    for (const config of hangarConfigs) {
      hangarWalls.set(config.areaId, createHangarWallMeshes(config))
    }
    const { allMeshes: hMeshes, wallToHangarMap } = getAggregatedWallCollision(hangarWalls)

    const hangarSerialized: SerializedWallGeometry = {
      meshes: serializeWallMeshes(hMeshes),
      wallInfo: Array.from(wallToHangarMap.entries()).map(([, info]) => ({
        areaId: info.areaId,
        side: info.side,
        isDoor: false,
      })),
    }

    // Build unit wall meshes
    const unitWalls = createAllUnitWallMeshes(unitConfigs)
    const { allMeshes: uMeshes, wallToUnitMap } = getAggregatedUnitWallCollision(unitWalls)

    const unitSerialized: SerializedWallGeometry = {
      meshes: serializeWallMeshes(uMeshes),
      wallInfo: Array.from(wallToUnitMap.entries()).map(([, info]) => ({
        areaId: info.areaId,
        side: info.side,
        unitId: info.unitId,
        isDoor: info.isDoor,
      })),
    }

    const transferables = [
      ...collectWallTransferables(hangarSerialized.meshes),
      ...collectWallTransferables(unitSerialized.meshes),
    ]

    const msg: WorkerInMessage = {
      type: 'UPDATE_WALL_GEOMETRY',
      hangarWalls: hangarSerialized,
      unitWalls: unitSerialized,
    }

    this.worker.postMessage(msg, transferables)
  }

  /**
   * Send obstacle geometry to the worker
   * Obstacles are treated like walls — pre-transformed, identity matrix
   */
  updateObstacleGeometry(
    serialized: SerializedObstacleGeometry
  ): void {
    if (this.fallbackMode || !this.worker) return

    const transferables: Transferable[] = []
    for (const mesh of serialized.meshes) {
      transferables.push(mesh.positions.buffer)
      if (mesh.indices) transferables.push(mesh.indices.buffer)
      transferables.push(mesh.worldMatrix.buffer)
    }

    const msg: WorkerInMessage = {
      type: 'UPDATE_OBSTACLE_GEOMETRY',
      obstacles: serialized,
    }

    this.worker.postMessage(msg, transferables)
  }

  /**
   * Request a collision check from the worker
   * Returns a promise that resolves with CollisionResult
   * Uses monotonic checkId to discard stale results
   */
  async checkCollisions(
    aircraft: PlacedAircraft3D[],
    sceneObjects: Map<string, THREE.Object3D>,
    hangarConfigs: HangarCollisionConfig[],
    movingAircraftId?: string,
    unitConfigs?: UnitCollisionConfig[]
  ): Promise<CollisionResult> {
    // Global pause guard: when collision detection is disabled (e.g. the Masters
    // event planner pauses it during bulk auto-placement), short-circuit every
    // check — both worker and synchronous paths — and report no collisions.
    if (!useCollisionStore.getState().isEnabled) {
      return {
        collisions: new Set<string>(),
        isPartialCheck: !!movingAircraftId,
        checkedAircraftIds: aircraft.map((a) => a.id),
        checkDurationMs: 0,
      }
    }

    // Fallback: run synchronous check on main thread
    if (this.fallbackMode || !this.worker || !this.ready) {
      return checkCollisions(aircraft, sceneObjects, hangarConfigs, movingAircraftId, unitConfigs)
    }

    const checkId = this.nextCheckId++
    const aircraftIds = aircraft.map((a) => a.id)
    const transforms = readAircraftTransforms(aircraftIds, sceneObjects)

    const msg: WorkerInMessage = {
      type: 'CHECK_COLLISION',
      checkId,
      transforms,
      movingAircraftId,
      proximityRadius: COLLISION_CHECK_RADIUS_METERS,
    }

    this.worker.postMessage(msg)

    return new Promise<CollisionResult>((resolve, reject) => {
      this.pendingChecks.set(checkId, { resolve, reject, checkId, aircraftIds, movingAircraftId })

      // Timeout: full checks get more time (18 aircraft = 153 pairs + walls)
      const timeoutMs = movingAircraftId ? 500 : 5000
      setTimeout(() => {
        if (this.pendingChecks.has(checkId)) {
          if (import.meta.env.DEV) {
            console.warn(`[CollisionWorker] checkId=${checkId} timed out after ${timeoutMs}ms`)
          }
          this.pendingChecks.delete(checkId)
          resolve({
            collisions: new Set<string>(),
            isPartialCheck: !!movingAircraftId,
            checkedAircraftIds: aircraftIds,
            checkDurationMs: 0,
          })
        }
      }, timeoutMs)
    })
  }

  /**
   * Dispose worker and clean up
   */
  dispose(): void {
    if (this.worker) {
      const msg: WorkerInMessage = { type: 'DISPOSE' }
      this.worker.postMessage(msg)
      this.worker.terminate()
      this.worker = null
    }

    this.isReady = false
    this.geometryReady = false
    this.fallbackMode = false

    // Reject all pending checks
    for (const pending of this.pendingChecks.values()) {
      pending.reject(new Error('Worker disposed'))
    }
    this.pendingChecks.clear()
  }

  // ============================================================================
  // Private
  // ============================================================================

  private handleMessage(msg: WorkerOutMessage): void {
    switch (msg.type) {
      case 'WORKER_READY': {
        this.isReady = true
        if (this.initResolve) {
          this.initResolve()
          this.initResolve = null
          this.initPromise = null
        }
        console.log('[CollisionWorker:PROD] Worker ready ✓')
        break
      }

      case 'GEOMETRY_READY': {
        // Only mark geometry ready when aircraft init completes (not wall/obstacle updates)
        if (msg.source === 'init') {
          this.geometryReady = true
        }
        if (import.meta.env.DEV) {
          console.log(`[CollisionWorker] Geometry ready (${msg.source}): ${msg.aircraftCount} aircraft, ${msg.wallCount} walls`)
        }
        break
      }

      case 'COLLISION_RESULT': {
        console.log(`[CollisionWorker:PROD] RESULT checkId=${msg.checkId}: ${msg.collidingIds.length} colliding, wallHits=${msg.wallHits.length}, obstacleHits=${msg.obstacleHits.length}, ${msg.durationMs.toFixed(1)}ms`)
        this.handleCollisionResult(msg)
        break
      }

      case 'WORKER_ERROR': {
        console.error('[CollisionWorker] Error:', msg.error)
        if (msg.fatal) {
          this.enableFallback()
        }
        // Reject all pending checks so they fail fast instead of timing out silently
        for (const [id, pending] of this.pendingChecks) {
          pending.resolve({
            collisions: new Set<string>(),
            isPartialCheck: false,
            checkedAircraftIds: [],
            checkDurationMs: 0,
          })
          this.pendingChecks.delete(id)
        }
        break
      }
    }
  }

  private handleCollisionResult(msg: CollisionResultMessage): void {
    // Discard stale results (from previous drag frames)
    if (msg.checkId <= this.lastResolvedCheckId) {
      return
    }
    this.lastResolvedCheckId = msg.checkId

    const pending = this.pendingChecks.get(msg.checkId)
    if (!pending) return

    this.pendingChecks.delete(msg.checkId)

    // Also discard any older pending checks
    for (const [id, p] of this.pendingChecks) {
      if (id < msg.checkId) {
        p.resolve({
          collisions: new Set<string>(),
          isPartialCheck: true,
          checkedAircraftIds: [],
          checkDurationMs: 0,
        })
        this.pendingChecks.delete(id)
      }
    }

    // Convert worker result to CollisionResult
    const wallCollisions = new Map<string, WallCollisionInfo>()
    for (const hit of msg.wallHits) {
      wallCollisions.set(hit.aircraftId, {
        side: hit.side,
        unitId: hit.unitId,
        areaId: hit.areaId,
        isDoor: hit.isDoor,
      })
    }

    const obstacleCollisions = new Map<string, ObstacleCollisionInfo>()
    for (const hit of msg.obstacleHits) {
      obstacleCollisions.set(hit.aircraftId, {
        obstacleId: hit.obstacleId,
        obstacleName: hit.obstacleName,
      })
    }

    const result: CollisionResult = {
      collisions: new Set(msg.collidingIds),
      isPartialCheck: !!pending.movingAircraftId,
      checkedAircraftIds: pending.aircraftIds,
      checkDurationMs: msg.durationMs,
      wallCollisions,
      obstacleCollisions,
    }

    pending.resolve(result)
  }

  private enableFallback(): void {
    if (this.fallbackMode) return

    console.warn('[CollisionWorker:PROD] ⚠ Falling back to synchronous collision detection')
    this.fallbackMode = true

    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }

    // Resolve any pending checks with empty results
    for (const pending of this.pendingChecks.values()) {
      pending.resolve({
        collisions: new Set<string>(),
        isPartialCheck: false,
        checkedAircraftIds: [],
        checkDurationMs: 0,
      })
    }
    this.pendingChecks.clear()

    // Resolve init if pending
    if (this.initResolve) {
      this.initResolve()
      this.initResolve = null
    }
  }
}

/** Singleton instance */
let instance: CollisionWorkerService | null = null

/**
 * Get the singleton CollisionWorkerService instance
 */
export function getCollisionWorkerService(): CollisionWorkerService {
  if (!instance) {
    instance = new CollisionWorkerService()
  }
  return instance
}

/**
 * Dispose the singleton instance
 */
export function disposeCollisionWorkerService(): void {
  if (instance) {
    instance.dispose()
    instance = null
  }
}
